import * as constants from "common/types/constants";
import { ITOConsumer, ITOOrdersService } from "lib/services";
import { useEffect, useState } from "react";

/**
 * Represents the display information for a hardware support order.
 *
 * @property {string} orderRef - A short reference identifier used to display the order (e.g., "Order #12345").
 * @property {string} orderTaxId - The internal taxonomy ID used to fetch or associate order data.
 * @property {string} orderDisplay - A human-readable label that includes details like product name or description.
 */
export interface OrderDisplayInfo {
  orderRef: string;
  orderTaxId: string;
  orderDisplay: string;
}

/**
 * Represents the result returned by the `useHardwareSupport` hook.
 *
 * @property {OrderDisplayInfo[]} orderList - Array of hardware orders available for support requests.
 * @property {boolean} isLoading - Indicates whether an async operation (like fetching orders) is in progress.
 * @property {(orderId: string, comment: string) => Promise<boolean>} submitComment - Function to submit a support comment for a specific order.
 * @property {boolean} hasError - Indicates if there was an error during an async operation.
 */
export interface useHardwareSupportResult {
  orderList: OrderDisplayInfo[];
  isLoading: boolean;
  submitComment: (orderId: string, comment: string) => Promise<boolean>;
  hasError: boolean;
}

/**
 * Custom React hook to interact with the ITO for hardware support.
 *
 * This hook:
 * - Fetches the current user's open hardware support orders.
 * - Allows submitting support comments for individual orders.
 *
 * @param {ITOConsumer} consumer - The ITO consumer used to initialize the ITO service.
 * @returns {useHardwareSupportResult} An object containing the list of orders, loading state,
 * error state, and a method to submit comments.
 */
export function useHardwareSupport(consumer: ITOConsumer): useHardwareSupportResult {
  const ito = new ITOOrdersService(consumer);
  const [orderList, setOrderList] = useState<{ orderRef: string; orderTaxId: string; orderDisplay: string }[]>(
    constants.EMPTY_ARRAY
  );
  const [isLoading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Fetch orders only once when component mounts
  useEffect(() => {
    setLoading(true);
    fetchOrders();
  }, []);

  /**
   * Fetches the current user’s open AMZNHAM orders and updates component state.
   *
   * This function:
   * 1. Build a filter object to request only “open” statuses.
   * 2. Call the ITO API client to retrieve up to DEFAULT_GET_ORDER_LIMIT orders.
   * 3. Map the raw API response into a simplified list of { orderRef, orderTaxId, orderDisplay }.
   * 4. Store the transformed list in state via setOrderList.
   * 5. On error, set the hasError flag so the UI can render an error banner.
   * 6. Always clear the loading flag when finished.
   *
   * @async
   * @function fetchOrders
   * @returns {Promise<void>} Resolves once orders have been fetched (or errored) and state is updated.
   */
  const fetchOrders = async () => {
    try {
      // Filter by open orders only, exclude final statuses
      const filters: { [key: string]: string[] } = {};
      filters.status = constants.AMZNHAM_OPEN_STATUSES;

      const result = await ito.GetOrders_My(
        constants.EMPTY_STRING,
        constants.DEFAULT_GET_ORDER_LIMIT,
        undefined,
        filters
      );
      const combinedList =
        result.orders?.map((order) => ({
          orderRef: order.orderReference ?? constants.EMPTY_STRING,
          orderTaxId: order.orderId ?? constants.EMPTY_STRING,
          orderDisplay: order.display ?? constants.EMPTY_STRING,
        })) ?? [];

      setOrderList(combinedList);
    } catch (error) {
      setHasError(true);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Submit a support comment for a given order.
   *
   * @param {string} orderTaxId - The unique tax ID of the order to which the comment belongs.
   * @param {string} comment - The text content of the comment.
   * @returns A Promise that resolves to:
   * - `true`  if the comment was posted successfully,
   * - `false` if an error occurred during submission.
   *
   * Side effects:
   * - Manages `loading` and `hasError` state flags for the UI.
   */
  const submitComment = async (orderTaxId: string, comment: string): Promise<boolean> => {
    let successfullySubmitted: boolean = true;
    setHasError(false);

    try {
      setLoading(true);
      const csrfToken = await ito.GetSecurityToken();
      await ito.PostComment(orderTaxId!, comment, csrfToken, constants.SUPPORT_TAGS);
    } catch (error) {
      successfullySubmitted = false;
      setHasError(true);
    } finally {
      setLoading(false);
    }
    return successfullySubmitted;
  };

  return {
    orderList,
    isLoading,
    submitComment,
    hasError,
  };
}
